import type { ReactNode } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import MailSettingsPage from "../mail-settings/ui";
import AuditLogPage from "../audit-log/ui";
import BusinessErrorsPage from "../business-errors/ui";
import { WorkspaceSettingsContent } from "../workspace-settings";
import UsersPanel from "./ui/UsersPanel";
import InvitesPanel from "./ui/InvitesPanel";
import DashboardPanel from "./ui/DashboardPanel";
import PlansPanel from "./ui/PlansPanel";
import CustomersPanel from "./ui/CustomersPanel";
import DatasourcesPanel from "./ui/DatasourcesPanel";
import BeasLlmModelsPanel from "./ui/BeasLlmModelsPanel";
import EmbedsPanel from "./ui/EmbedsPanel";
import IntegrationsPanel from "./ui/IntegrationsPanel";
import UiDocsRagPanel from "./ui/UiDocsRagPanel";
import Stack from "@mui/material/Stack";

export type SuperAdminSection = {
  /** URL segment and route key suffix. */
  key: string;
  /** Sidenav / route key (must be in SUPER_ADMIN_MENU_KEYS). */
  routeKey: string;
  /** i18n key under superAdmin.tabs.* */
  labelKey: string;
  /** Hide from menu and redirect away on on-prem deployments. */
  saasOnly?: boolean;
  icon: ReactNode;
  render: () => ReactNode;
};

/** Platform portal sections — each is a left-nav item under Super Admin. */
export const SUPER_ADMIN_SECTIONS: SuperAdminSection[] = [
  {
    key: "dashboard",
    routeKey: "sa-dashboard",
    labelKey: "dashboard",
    saasOnly: true,
    icon: <DashboardOutlinedIcon fontSize="small" />,
    render: () => <DashboardPanel />,
  },
  {
    key: "plans",
    routeKey: "sa-plans",
    labelKey: "plans",
    saasOnly: true,
    icon: <SellOutlinedIcon fontSize="small" />,
    render: () => <PlansPanel />,
  },
  {
    key: "customers",
    routeKey: "sa-customers",
    labelKey: "customers",
    saasOnly: true,
    icon: <StorefrontOutlinedIcon fontSize="small" />,
    render: () => <CustomersPanel />,
  },
  {
    key: "datasources",
    routeKey: "sa-datasources",
    labelKey: "datasources",
    icon: <StorageOutlinedIcon fontSize="small" />,
    render: () => (
      <Stack spacing={2}>
        <DatasourcesPanel />
        <BeasLlmModelsPanel />
      </Stack>
    ),
  },
  {
    key: "ui-docs",
    routeKey: "sa-ui-docs",
    labelKey: "uiDocs",
    icon: <MenuBookOutlinedIcon fontSize="small" />,
    render: () => <UiDocsRagPanel />,
  },
  {
    key: "mail",
    routeKey: "sa-mail",
    labelKey: "mail",
    icon: <EmailOutlinedIcon fontSize="small" />,
    render: () => <MailSettingsPage />,
  },
  {
    key: "workspaces",
    routeKey: "sa-workspaces",
    labelKey: "workspaces",
    saasOnly: true,
    icon: <BusinessOutlinedIcon fontSize="small" />,
    render: () => <WorkspaceSettingsContent />,
  },
  {
    key: "users",
    routeKey: "sa-users",
    labelKey: "users",
    icon: <PeopleOutlinedIcon fontSize="small" />,
    render: () => <UsersPanel />,
  },
  {
    key: "invites",
    routeKey: "sa-invites",
    labelKey: "invites",
    saasOnly: true,
    icon: <MailOutlineOutlinedIcon fontSize="small" />,
    render: () => <InvitesPanel />,
  },
  {
    key: "embeds",
    routeKey: "sa-embeds",
    labelKey: "embeds",
    icon: <CodeOutlinedIcon fontSize="small" />,
    render: () => <EmbedsPanel />,
  },
  {
    key: "integrations",
    routeKey: "sa-integrations",
    labelKey: "integrations",
    icon: <HubOutlinedIcon fontSize="small" />,
    render: () => <IntegrationsPanel />,
  },
  {
    key: "audit",
    routeKey: "sa-audit",
    labelKey: "audit",
    icon: <HistoryOutlinedIcon fontSize="small" />,
    render: () => <AuditLogPage />,
  },
  {
    key: "errors",
    routeKey: "sa-errors",
    labelKey: "errors",
    icon: <BugReportOutlinedIcon fontSize="small" />,
    render: () => <BusinessErrorsPage />,
  },
];

export const SUPER_ADMIN_SECTION_KEYS = SUPER_ADMIN_SECTIONS.map((s) => s.key);

export function defaultSuperAdminSection(onPrem: boolean): string {
  return onPrem ? "datasources" : "dashboard";
}

export function findSuperAdminSection(key: string | undefined): SuperAdminSection | undefined {
  if (!key) return undefined;
  return SUPER_ADMIN_SECTIONS.find((s) => s.key === key);
}
